import React, { useState, useRef, useEffect } from 'react';
import { IconButton, Slider, Typography, Box, List, ListItem, ListItemText, Collapse } from '@mui/material';
import {
    PlayArrow,
    Pause,
    SkipNext,
    SkipPrevious,
    VolumeUp,
    VolumeOff,
    PlaylistPlay,
    Close,
    ExpandLess,
    ExpandMore,
    Minimize,
    KeyboardArrowRight,
} from '@mui/icons-material';
import '../styles/MusicPlayer.scss';

const songs = [
    {
        title: "Audrey - Chill Acoustic Guitar",
        file: "/music/[No Copyright Music] Audrey - Chill Acoustic Guitar Music (For Nature Vlogs & Traveling!).mp3"
    },
    {
        title: "Pulse - Emotional Electric Guitar",
        file: "/music/[No Copyright Music] Pulse - Emotional Electric Guitar (Perfect for Documentaries & Nature).mp3"
    },
    {
        title: "Mango - Slow Acoustic Folk",
        file: "/music/[Background Music] Mango - Slow Acoustic Folk 🍏  Relaxing Guitar No Copyright Music.mp3"
    },
    {
        title: "Oasis - Acoustic Guitar House",
        file: "/music/[Background Music] Oasis - Acoustic Guitar House & Dance Music (Ideal for Traveling Vlogs!).mp3"
    },
    {
        title: "Mild - Simple & Inspirational",
        file: "/music/[No Copyright Music] Mild - Simple & Inspirational Acoustic Guitar  Background Music.mp3"
    },
    {
        title: "Seven - Acoustic Guitar Folk",
        file: "/music/[No Copyright Music] Seven - Acoustic Guitar Folk  Traveling Background Music.mp3"
    },
    {
        title: "Lean On - Chill Indie Music",
        file: "/music/[No Copyright Music] Lean On - Chill Indie Music  Ambient Guitar #8.mp3"
    },
    {
        title: "Inner Light - Ambient Guitar",
        file: "/music/[No Copyright Music] Inner Light - Ambient Guitar #6 (Meditation & Stress Relief).mp3"
    },
    {
        title: "Daisy - Chill Acoustic Folk",
        file: "/music/[No Copyright Music] Daisy - Chill Acoustic Folk  Vlogging Background Music.mp3"
    },
    {
        title: "Anne - Slow Acoustic Folk",
        file: "/music/[No Copyright Music] Anne - Slow Acoustic Folk  Background Music.mp3"
    },
    {
        title: "I Just Want Quiet - Slow Acoustic",
        file: "/music/[No Copyright Music] I Just Want Quiet - Slow Acoustic Guitar  Calm Background Music.mp3"
    },
    {
        title: "Off She Goes - Acoustic Lo-Fi",
        file: "/music/[No Copyright Music] Off She Goes - Acoustic meets Lo-Fi  Relaxing Background Music.mp3"
    },
    {
        title: "Medved - Calm Acoustic Guitar",
        file: "/music/[No Copyright Music] Medved - Calm Acoustic Guitar  Background Music.mp3"
    },
    {
        title: "Sonhe - Relaxing Acoustic Folk",
        file: "/music/[No Copyright Music] Sonhe - Relaxing Acoustic Folk  Background Music.mp3"
    },
    {
        title: "White - Chilling Guitar",
        file: "/music/[No Copyright Music] White - Chilling Guitar.mp3"
    }
];

const MusicPlayer = () => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentSongIndex, setCurrentSongIndex] = useState(Math.floor(Math.random() * songs.length));
    const [volume, setVolume] = useState(0.15);
    const [isMuted, setIsMuted] = useState(false);
    const [isMinimized, setIsMinimized] = useState(true);
    const [showPlaylist, setShowPlaylist] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isMobile, setIsMobile] = useState(false);
    const audioRef = useRef(new Audio());
    const audioCache = useRef(new Map());

    // Mobilnézet ellenőrzése
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 900);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        const initializeFirstSongs = async () => {
            if (isMobile) return;
            
            setIsLoading(true);
            const firstAudio = new Audio(songs[currentSongIndex].file);
            firstAudio.volume = volume;
            await firstAudio.load();
            audioCache.current.set(currentSongIndex, firstAudio);
            
            // Preload next 2 songs
            const nextIndices = [
                (currentSongIndex + 1) % songs.length,
                (currentSongIndex + 2) % songs.length
            ];
            
            for (const index of nextIndices) {
                const audio = new Audio();
                audio.preload = "metadata";
                audio.src = songs[index].file;
                audioCache.current.set(index, audio);
            }
            setIsLoading(false);
        };

        initializeFirstSongs();

        return () => {
            audioCache.current.forEach(audio => {
                audio.pause();
                audio.src = '';
            });
            audioCache.current.clear();
        };
    }, [isMobile]);

    useEffect(() => {
        if (isMobile) return;

        const currentAudio = audioCache.current.get(currentSongIndex) || new Audio(songs[currentSongIndex].file);
        audioRef.current = currentAudio;
        
        if (!audioCache.current.has(currentSongIndex)) {
            currentAudio.preload = "auto";
            audioCache.current.set(currentSongIndex, currentAudio);
        }

        currentAudio.volume = isMuted ? 0 : volume;

        const handleEnded = () => {
            handleNext();
        };

        currentAudio.addEventListener('ended', handleEnded);
        preloadNextSong();

        return () => {
            currentAudio.removeEventListener('ended', handleEnded);
        };
    }, [currentSongIndex, isMobile]);

    useEffect(() => {
        if (isMobile) return;
        const audio = audioRef.current;
        audio.volume = isMuted ? 0 : volume;
    }, [volume, isMuted, isMobile]);

    const preloadNextSong = () => {
        const nextIndex = (currentSongIndex + 1) % songs.length;
        if (!audioCache.current.has(nextIndex)) {
            const audio = new Audio();
            audio.preload = "metadata";
            audio.src = songs[nextIndex].file;
            audioCache.current.set(nextIndex, audio);
        }
    };

    const togglePlay = () => {
        const audio = audioRef.current;
        if (isPlaying) {
            audio.pause();
        } else {
            audio.play().catch(error => {
                console.error('Lejátszási hiba:', error);
                setIsPlaying(false);
            });
        }
        setIsPlaying(!isPlaying);
    };

    const handleNext = () => {
        const nextIndex = (currentSongIndex + 1) % songs.length;
        if (audioCache.current.has(nextIndex)) {
            const currentAudio = audioRef.current;
            currentAudio.pause();
            setCurrentSongIndex(nextIndex);
            if (isPlaying) {
                const nextAudio = audioCache.current.get(nextIndex);
                nextAudio.currentTime = 0;
                nextAudio.play();
            }
        } else {
            setCurrentSongIndex(nextIndex);
        }
    };

    const handlePrevious = () => {
        const prevIndex = currentSongIndex === 0 ? songs.length - 1 : currentSongIndex - 1;
        if (audioCache.current.has(prevIndex)) {
            const currentAudio = audioRef.current;
            currentAudio.pause();
            setCurrentSongIndex(prevIndex);
            if (isPlaying) {
                const prevAudio = audioCache.current.get(prevIndex);
                prevAudio.currentTime = 0;
                prevAudio.play();
            }
        } else {
            setCurrentSongIndex(prevIndex);
        }
    };

    const handleVolumeChange = (event, newValue) => {
        setVolume(newValue);
        setIsMuted(false);
    };

    const toggleMute = () => {
        setIsMuted(!isMuted);
    };

    const handleSongSelect = (index) => {
        if (index === currentSongIndex) {
            togglePlay();
            return;
        }

        const currentAudio = audioRef.current;
        currentAudio.pause();
        setCurrentSongIndex(index);
        
        if (audioCache.current.has(index)) {
            const selectedAudio = audioCache.current.get(index);
            selectedAudio.currentTime = 0;
            if (!selectedAudio.readyState) {
                selectedAudio.load();
            }
            selectedAudio.play().catch(console.error);
            setIsPlaying(true);
        } else {
            const newAudio = new Audio(songs[index].file);
            newAudio.volume = isMuted ? 0 : volume;
            audioCache.current.set(index, newAudio);
            newAudio.play().catch(console.error);
            setIsPlaying(true);
        }
    };

    const toggleMinimize = () => {
        setIsMinimized(!isMinimized);
        setShowPlaylist(false);
    };

    if (isMobile) {
        return null;
    }

    return (
        <div className={`music-player ${isMinimized ? 'minimized' : ''}`}>
            {isMinimized ? (
                <div className="minimized-controls">
                    <IconButton onClick={handlePrevious}>
                        <SkipPrevious />
                    </IconButton>
                    <IconButton onClick={togglePlay} disabled={isLoading}>
                        {isPlaying ? <Pause /> : <PlayArrow />}
                    </IconButton>
                    <IconButton onClick={handleNext}>
                        <SkipNext />
                    </IconButton>
                    <Typography className="song-title">
                        {songs[currentSongIndex].title}
                    </Typography>
                    <IconButton onClick={toggleMinimize} className="expand-button">
                        <ExpandMore />
                    </IconButton>
                </div>
            ) : (
                <Box className="player-content">
                    <Box className="player-header">
                        <Typography variant="subtitle1" className="now-playing">
                            {songs[currentSongIndex].title}
                        </Typography>
                        <IconButton onClick={toggleMinimize}>
                            <ExpandLess />
                        </IconButton>
                    </Box>
                    
                    <Box className="controls">
                        <IconButton onClick={handlePrevious}>
                            <SkipPrevious />
                        </IconButton>
                        <IconButton onClick={togglePlay} className="play-button">
                            {isPlaying ? <Pause /> : <PlayArrow />}
                        </IconButton>
                        <IconButton onClick={handleNext}>
                            <SkipNext />
                        </IconButton>
                    </Box>

                    <Box className="volume-control">
                        <IconButton onClick={toggleMute}>
                            {isMuted || volume === 0 ? <VolumeOff /> : <VolumeUp />}
                        </IconButton>
                        <Slider
                            value={isMuted ? 0 : volume}
                            onChange={handleVolumeChange}
                            min={0}
                            max={1}
                            step={0.01}
                        />
                    </Box>

                    <Box className="playlist-section">
                        <Box className="playlist-header">
                            <Typography variant="subtitle2">Lejátszási lista</Typography>
                            <IconButton
                                onClick={() => setShowPlaylist(!showPlaylist)}
                            >
                                {showPlaylist ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                        </Box>
                        <Collapse in={showPlaylist}>
                            <List className="playlist">
                                {songs.map((song, index) => (
                                    <ListItem
                                        key={index}
                                        button
                                        onClick={() => handleSongSelect(index)}
                                        selected={currentSongIndex === index}
                                    >
                                        <ListItemText 
                                            primary={song.title}
                                            className="song-title"
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Collapse>
                    </Box>
                </Box>
            )}
        </div>
    );
};

export default MusicPlayer; 